import { Button, ConfirmationDialog, MarkdownText } from 'src/components';
import { type ChangeEvent, Fragment, useEffect, useState } from 'react';
import {
	Checkbox,
	Divider,
	Drawer,
	FormControlLabel,
	Grid,
	Grow,
	Hidden,
	Paper,
	Radio,
	RadioGroup,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@material-ui/core';
import {
	type IAnswer,
	type ICourse,
	type IQuestion,
	type ISavedExam,
	type ISection,
	type IUserAnswer,
} from 'src/models';
import { ChevronRight as RightIcon } from '@material-ui/icons';
import { secureLocalStorage } from 'src/helpers';
import { useHistory, useParams } from 'react-router-dom';
import ListIcon from '@material-ui/icons/ListAlt';
import _ from 'lodash';
import useStyles from './Exam-styles';

import { type RootState } from 'src/store';
import { useDispatch, useSelector } from 'react-redux';
import answerActions from 'src/store/Answers/actions';
import courseActions, { type Type } from 'src/store/Courses/actions';

function Exam() {
	const { courseId, sectionId }: any = useParams();
	const history = useHistory();
	const classes = useStyles();
	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();

	const exam: ICourse = useSelector((state: RootState) => state.courses.exam);
	const course: ICourse = useSelector((state: RootState) => state.courses.course);
	const sections = useSelector((state: RootState) => state.sections.sections);
	const examTracker = useSelector((state: RootState) => state.courses.examTracker);
	const userId: string = useSelector((state: RootState) => state.auth.data.id);

	const set = (type: Type, value: any) => dispatch(courseActions.set(type, value));
	const getExam = (id: number, sectionId?: number) => dispatch(courseActions.getExam(id, sectionId));
	const setExam = (exam: ICourse) => dispatch(courseActions.setExam(exam));
	const getCourse = (id: number, userId?: string) => dispatch(courseActions.getOne(id, userId));
	const setAnsweredSection = (section?: ISection, isSaved?: boolean) =>
		dispatch(courseActions.setAnsweredSection(section, isSaved));
	const saveUserCourseAnswers = (userAnswers: IUserAnswer[], courseId: number, userId: string) =>
		dispatch(answerActions.saveUserCourseAnswers(userAnswers, courseId, userId));
	const saveUserSectionAnswers = (userAnswers: IUserAnswer[], sectionId: number, userId: string) =>
		dispatch(answerActions.saveUserSectionAnswers(userAnswers, sectionId, userId));

	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [currentSection, setCurrentSection] = useState<ISection>();
	const [sectionsOpen, setSectionsOpen] = useState(false);
	const isLast = isLastSection();

	useEffect(() => {
		handleGetExam();

		if (_.isEmpty(course)) {
			getCourse(courseId, userId);
		}
	}, [courseId, sectionId]);

	useEffect(() => {
		if (!_.isEmpty(course)) {
			if ((course.attempt || 0) >= (course.maxAttempts || 0)) history.push(`/app/courses/${courseId}`);
		}
	}, [course]);

	function isLastSection() {
		const sections: ISection[] = exam.sections || [];
		const lastSection: ISection = sections[sections.length - 1];

		return lastSection?.id === currentSection?.id;
	}

	function handleSectionManually(section: ISection) {
		setAnsweredSection(currentSection);
		setCurrentSection(section);
	}

	function handleSingleAnswer(questionIndex: number, answerId: number) {
		const _currentSection = { ...currentSection };
		const _questions = _currentSection.questions ?? [];
		const answerIndex = _questions[questionIndex].answers?.findIndex((answer) => answer.id == answerId);
		const question = _questions[questionIndex];

		_.map(question.answers, (answer) => (answer.isSelected = false));

		if (
			answerIndex !== undefined &&
			_currentSection.questions &&
			_currentSection.questions[questionIndex].answers
		) {
			_currentSection.questions[questionIndex].answers![answerIndex].isSelected = true;
			setCurrentSection(_currentSection);
		}
	}

	function handleMultipleAnswer(questionIndex: number, answerIndex: number, value: boolean) {
		const _currentSection = { ...currentSection };

		if (_currentSection.questions && _currentSection.questions[questionIndex].answers) {
			_currentSection.questions[questionIndex].answers![answerIndex].isSelected = value;
			setCurrentSection(_currentSection);
		}
	}

	function handleNext() {
		if (isLast) {
			finishExam();
		} else {
			setAnsweredSection(currentSection);
			const currentSectionIndex =
				exam.sections?.findIndex((section: ISection) => section.id === currentSection?.id) ?? -1;

			if (exam.sections && exam.sections[currentSectionIndex + 1]) {
				setCurrentSection(exam.sections[currentSectionIndex + 1]);
			}
		}
	}

	function isValid() {
		let isValid = true;
		let answer;
		currentSection?.questions?.find((question: IQuestion) => {
			answer = question.answers?.find((answer: IAnswer) => answer.isSelected);
			if (answer == null) isValid = false;
		});

		return isValid;
	}

	function finishExam() {
		const answers: IUserAnswer[] = [];

		if (sectionId) {
			const section = sections.find((section: ISection) => section.id == sectionId);

			currentSection?.questions?.forEach((question: IQuestion) => {
				question.answers?.forEach((answer: IAnswer) => {
					if (answer.isSelected) {
						answers.push({
							answerId: answer.id,
							attempt: (section?.attempt || 0) + 1,
							isCorrect: answer.isCorrect,
							questionId: question.id,
							userId,
						});
					}
				});
			});

			saveUserSectionAnswers(answers, sectionId, userId);
		} else {
			exam.sections?.forEach((section: ISection) => {
				section.questions?.forEach((question: IQuestion) => {
					question.answers?.forEach((answer: IAnswer) => {
						if (answer.isSelected) {
							answers.push({
								answerId: answer.id,
								attempt: (course?.attempt || 0) + 1,
								isCorrect: answer.isCorrect,
								questionId: question.id,
								userId,
							});
						}
					});
				});
			});

			saveUserCourseAnswers(answers, courseId, userId);
		}
	}

	function startExam() {
		if (exam.sections === null) return;
		if (exam.sections?.length === 0) return;
		setCurrentSection(exam.sections != null ? exam.sections[0] : {});
		set('examTracker', { ...examTracker, startedAt: new Date() });
	}

	function saveAdvance() {
		setAnsweredSection(currentSection, true);
	}

	function handleGetExam() {
		const savedExams: ISavedExam[] = secureLocalStorage.get('ues') || [];

		const savedExam: ISavedExam =
			savedExams.find(
				(savedExam: ISavedExam) => savedExam.userId == userId && savedExam.exam?.id == courseId,
			) ?? {};

		if (_.isEmpty(savedExam)) {
			getExam(courseId, sectionId);
		} else {
			setExam(savedExam.exam ?? {});
		}
	}

	const sectionsContent = (
		<>
			<Stepper
				activeStep={currentSection?.id}
				className={classes.stepsContainer}
				elevation={1}
				orientation={'vertical'}
			>
				{exam.sections?.map((section: ISection) => (
					<Step color={'secondary'} completed={isValid()} key={section.id}>
						<StepLabel
							className={classes.activeSection}
							onClick={() => {
								handleSectionManually(section);
								if (sectionsOpen) setSectionsOpen(false);
							}}
						>
							{section.name}
						</StepLabel>
					</Step>
				))}
			</Stepper>

			<div className={classes.saveContainer}>
				<Button
					backgroundColor={'secondary'}
					onClick={() => {
						setConfirmationOpen(true);
					}}
				>
					Guardar avance
				</Button>
			</div>
		</>
	);

	return (
		<Grid container spacing={2}>
			<ConfirmationDialog
				buttonMessage={'Guardar'}
				message={
					'Para continuar el examen en la siguiente ocasión es necesario que sea en el mismo dispositivo, en el mismo navegador y no comenzar otro examen, de lo contrario, se perderá el avance.'
				}
				onClose={() => {
					setConfirmationOpen(false);
				}}
				onConfirm={saveAdvance}
				open={confirmationOpen}
				title={'¿Deseas guardar el avance?'}
			/>

			<Grid item md={3} xs={12}>
				<Hidden mdDown>{sectionsContent}</Hidden>

				<Hidden lgUp>
					<Button
						backgroundColor={'white'}
						fullWidth
						onClick={() => {
							setSectionsOpen(true);
						}}
						startIcon={<ListIcon />}
					>
						Secciones
					</Button>

					<Drawer
						className={classes.drawer}
						classes={{
							paper: classes.drawerPaper,
						}}
						onClose={() => {
							setSectionsOpen(false);
						}}
						open={sectionsOpen}
						variant={'temporary'}
					>
						{sectionsContent}
					</Drawer>
				</Hidden>
			</Grid>

			<Grid container item md={9} spacing={2}>
				{currentSection ? (
					<>
						{currentSection?.questions?.map((question: IQuestion, questionIndex: number) => (
							<Grid item key={question.id} xs={12}>
								<Grow
									in
									style={{ transformOrigin: '0 0 0' }}
									{...{
										timeout: questionIndex + 1 * 1000,
									}}
								>
									<Paper className={classes.questionCard}>
										<MarkdownText value={question.name} />

										<Divider className={classes.divider} />

										{question.isMultiple ? (
											question.answers?.map((answer: IAnswer, answerIndex: number) => (
												<div
													key={answer.id}
													style={{
														width: '100%',
													}}
												>
													<FormControlLabel
														control={
															<Checkbox
																checked={answer.isSelected || false}
																onChange={(event: any) => {
																	handleMultipleAnswer(
																		questionIndex,
																		answerIndex,
																		event.target.checked,
																	);
																}}
															/>
														}
														label={answer.name}
													/>
												</div>
											))
										) : (
											<RadioGroup
												onChange={(event: ChangeEvent<HTMLInputElement>) => {
													handleSingleAnswer(
														questionIndex,
														Number(event.target.value),
													);
												}}
												value={
													question.answers?.find(
														(answer: IAnswer) => answer.isSelected,
													)?.id || 0
												}
											>
												{question.answers?.map((answer: IAnswer) => (
													<FormControlLabel
														control={
															<Radio
																checked={
																	answer.id ==
																	question.answers?.find(
																		(answer: IAnswer) =>
																			answer.isSelected,
																	)?.id
																}
															/>
														}
														key={answer.id}
														label={answer.name}
														value={answer.id}
													/>
												))}
											</RadioGroup>
										)}
									</Paper>
								</Grow>
							</Grid>
						))}

						{currentSection?.id && (
							<div className={classes.buttons}>
								<Button
									actionType={isLast ? 'save' : undefined}
									backgroundColor={'secondary'}
									onClick={handleNext}
									// Disabled={!isValid()}
									endIcon={isLast ? <Fragment /> : <RightIcon />}
								>
									{isLast ? 'Terminar' : 'Siguiente'}
								</Button>
							</div>
						)}
					</>
				) : (
					<Grid item xs={12}>
						<Grow in style={{ transformOrigin: '0 0 0' }}>
							<Paper className={classes.questionCard}>
								<MarkdownText value={course.instructions} />

								<div className={classes.buttons}>
									<Typography style={{ fontSize: 14 }}>
										{' '}
										{`Intento ${course.exam === 'global'
											? course?.attempt
											: sections.find(
												(section: ISection) => section.id == sectionId
											)?.attempt
											}/${course.maxAttempts}`}{' '}
									</Typography>
								</div>
							</Paper>
						</Grow>

						<div className={classes.buttons}>
							<Button actionType={'save'} backgroundColor={'secondary'} onClick={startExam}>
								Comenzar
							</Button>
						</div>
					</Grid>
				)}
			</Grid>
		</Grid>
	);
}

export default Exam;
